/**
 * The CAD instances the desktop can currently see.
 *
 * `unsupported` is a state of its own, and the reason the hook exists at all.
 * In a browser there is no `window.bimgo`; against a desktop older than bridge
 * v4 there is no `hosts` channel. Neither can answer the question, and both are
 * different from a desktop that looked and found nothing — "no CAD host" is
 * M1's exit criterion and has to be *true*, not merely rendered (Corrections
 * #23). Collapsing them into an empty list is the one thing this must not do.
 *
 * Polling, rather than a pushed event, because the manifest directory is
 * written by a separate process (the plugin) and the desktop does not watch it.
 * The interval is idle-cheap but not free — each pass costs a process query and
 * a TCP probe per candidate — so it stops while the window is hidden and never
 * runs two passes at once. A slow pass delays the next one; it does not queue
 * behind it.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import type { CadInstance } from "../bimgo";

export type HostsState =
  | { kind: "unsupported" }
  | { kind: "loading" }
  | { kind: "error"; message: string }
  | { kind: "loaded"; instances: CadInstance[] };

/** Long enough that the PowerShell pass is not the machine's main activity. */
const POLL_MS = 15_000;

/** Equal as the UI reads them — identity, address, document, version. */
export function sameInstances(a: readonly CadInstance[], b: readonly CadInstance[]): boolean {
  if (a.length !== b.length) return false;
  const key = (i: CadInstance) =>
    `${i.sessionId}|${i.pid}|${i.host}:${i.port}|${i.hostVersion ?? i.revitVersion ?? ""}|${i.documentId ?? ""}|${i.documentTitle}|${i.host_kind}`;
  return a.every((i, index) => key(i) === key(b[index]!));
}

/** What the pane and the instance card both read. */
export type HostsView = HostsState & { reload: () => void; refreshing: boolean };

export function useHosts(enabled = true): HostsView {
  const hosts = window.bimgo?.hosts;
  const [state, setState] = useState<HostsState>(hosts ? { kind: "loading" } : { kind: "unsupported" });
  const [refreshing, setRefreshing] = useState(false);

  /** Guards against overlapping passes; a poll tick during one is dropped. */
  const inFlight = useRef(false);
  const mounted = useRef(true);
  const lifecycle = useRef(0);

  const refresh = useCallback(async () => {
    if (!enabled || !hosts || inFlight.current) return;
    const currentLifecycle = lifecycle.current;
    inFlight.current = true;
    setRefreshing(true);
    try {
      const instances = await hosts.list();
      // The same instances are the same state: a poll (and the visibility
      // refresh on every focus) that replaced the array regardless gave the
      // shell a new `selection` object each time, and everything downstream of
      // it re-rendered for nothing.
      if (mounted.current && currentLifecycle === lifecycle.current) setState((prev) => (prev.kind === "loaded" && sameInstances(prev.instances, instances) ? prev : { kind: "loaded", instances }));
    } catch (error: unknown) {
      if (mounted.current && currentLifecycle === lifecycle.current) {
        setState({
          kind: "error",
          message: error instanceof Error ? error.message : String(error),
        });
      }
    } finally {
      if (currentLifecycle === lifecycle.current) {
        inFlight.current = false;
        if (mounted.current) setRefreshing(false);
      }
    }
  }, [hosts, enabled]);

  useEffect(() => {
    mounted.current = enabled;
    if (!enabled) {
      setState(hosts ? { kind: "loading" } : { kind: "unsupported" });
      setRefreshing(false);
      return () => { mounted.current = false; };
    }
    if (!hosts) return () => undefined;

    void refresh();
    const timer = window.setInterval(() => {
      if (document.visibilityState === "visible") void refresh();
    }, POLL_MS);

    // Coming back to the window is exactly when the answer is most likely to
    // have changed — the user just switched away to start or close Revit.
    const onVisible = () => {
      if (document.visibilityState === "visible") void refresh();
    };
    document.addEventListener("visibilitychange", onVisible);
    // A desktop window stays "visible" behind the CAD host, so switching back from it
    // fires no visibilitychange — only focus. Without this, a turn typed right after
    // closing one AutoCAD and opening another went out with the closed one's id for up
    // to a poll, and the agent dropped that vendor's tools (#189).
    window.addEventListener("focus", onVisible);

    return () => {
      mounted.current = false;
      // Ignore a previous login's pending response and allow the next login to start fresh.
      lifecycle.current += 1;
      inFlight.current = false;
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, [hosts, refresh, enabled]);

  return { ...state, reload: () => void refresh(), refreshing };
}
